/**
 * Idea Detail Screen - MOBILE ONLY
 * 
 * Full view of an idea with voting, comments, and activity.
 * Allows editing status, adding comments, and viewing history.
 */

import React, { useState, useCallback } from 'react';
import { View, Text, ScrollView, Pressable, ActivityIndicator, Alert, RefreshControl } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { color, icon } from '../../../../shared/tokens/mobile';
import { UpdateIdeaRequest, VoteType } from '../../../../shared/types/idea';
import { useIdeaDetail } from '../../../../hooks/useIdeaDetail';
import { ScreenContainer, ScreenHeader } from '../../../../components/v2/mobile';
import { 
  IdeaVoteBar, 
  IdeaComments, 
  IdeaActivity, 
  EditIdeaModal,
  CATEGORY_CONFIG, 
  STATUS_CONFIG, 
  PRIORITY_CONFIG,
} from '../../../../components/v2/ideas';
import { styles } from '../../../../components/v2/ideas/styles/ideaDetailStyles';
import { formatUserContent } from '../../../../shared/utils/textHelpers';

// Default configs for safety
const DEFAULT_CATEGORY = { key: 'general', label: 'General', icon: 'bulb-outline', color: '#6B6B6B' };
const DEFAULT_STATUS = { key: 'open', label: 'Open', icon: 'radio-button-off-outline', color: '#4A6B82' };
const DEFAULT_PRIORITY = { key: 'medium', label: 'Medium', color: '#4A6B82' };

export default function IdeaDetailScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const ideaId = id ? parseInt(id, 10) : 0;

  const {
    idea, comments, activities, loading, updating, voting, commenting,
    loadingComments, loadingActivity, error, updateIdea, deleteIdea, voteIdea, addComment, refresh,
  } = useIdeaDetail({ ideaId });

  const [showEditModal, setShowEditModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'comments' | 'activity'>('comments');

  const handleBack = useCallback(() => router.back(), [router]);

  const handleMore = useCallback(() => {
    Alert.alert('Idea Actions', undefined, [
      { text: 'Edit Idea', onPress: () => setShowEditModal(true) },
      { text: 'Delete Idea', style: 'destructive', onPress: () => {
        Alert.alert('Delete Idea?', 'This action cannot be undone.', [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Delete', style: 'destructive', onPress: async () => {
            const success = await deleteIdea();
            if (success) router.back();
          }},
        ]);
      }},
      { text: 'Cancel', style: 'cancel' },
    ]);
  }, [deleteIdea, router]);

  const handleVote = useCallback(async (voteType: VoteType | null) => { await voteIdea(voteType); }, [voteIdea]);
  const handleSave = useCallback(async (data: UpdateIdeaRequest) => await updateIdea(data), [updateIdea]);
  const handleAddComment = useCallback(async (content: string, parentId?: number) => await addComment(content, parentId), [addComment]);

  if (loading) {
    return (
      <ScreenContainer>
        <ScreenHeader title="" onBack={handleBack} loading />
        <View style={styles.centerContainer}><ActivityIndicator size="large" color={color.primary} /></View>
      </ScreenContainer>
    );
  }

  if (error || !idea) {
    return (
      <ScreenContainer>
        <ScreenHeader title="Idea" onBack={handleBack} />
        <View style={styles.centerContainer}>
          <Ionicons name="alert-circle-outline" size={icon.xxl} color={color.error} />
          <Text style={styles.errorText}>{error || 'Idea not found'}</Text>
          <Pressable onPress={refresh} style={styles.retryButton}><Text style={styles.retryButtonText}>Try Again</Text></Pressable>
        </View>
      </ScreenContainer>
    );
  }

  const categoryConfig = CATEGORY_CONFIG[idea.category] || DEFAULT_CATEGORY;
  const statusConfig = STATUS_CONFIG[idea.status] || DEFAULT_STATUS;
  const priorityConfig = PRIORITY_CONFIG[idea.priority] || DEFAULT_PRIORITY;
  const authorName = idea.author?.display_name || idea.author?.email?.split('@')[0] || 'Unknown';
  const createdDate = idea.created_at ? new Date(idea.created_at).toLocaleDateString() : '';

  return (
    <ScreenContainer>
      <ScreenHeader title="Idea" onBack={handleBack} onMore={handleMore} />
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={false} onRefresh={refresh} tintColor={color.primary} />}>
        {/* HEADER CARD */}
        <View style={styles.headerCard}>
          <View style={styles.badgeRow}>
            <View style={[styles.categoryBadge, { backgroundColor: categoryConfig.color + '15' }]}>
              <Ionicons name={categoryConfig.icon as any} size={14} color={categoryConfig.color} />
              <Text style={[styles.badgeText, { color: categoryConfig.color }]}>{categoryConfig.label}</Text>
            </View>
            <View style={[styles.statusBadge, { backgroundColor: statusConfig.color + '15' }]}>
              <Ionicons name={statusConfig.icon as any} size={14} color={statusConfig.color} />
              <Text style={[styles.badgeText, { color: statusConfig.color }]}>{statusConfig.label}</Text>
            </View>
            {idea.priority !== 'medium' && (
              <View style={[styles.priorityBadge, { backgroundColor: priorityConfig.color + '15' }]}>
                <Text style={[styles.badgeText, { color: priorityConfig.color }]}>{priorityConfig.label}</Text>
              </View>
            )}
          </View>
          <Text style={styles.title}>{formatUserContent(idea.title)}</Text>
          {idea.description && <Text style={styles.description}>{formatUserContent(idea.description)}</Text>}
          <View style={styles.authorRow}>
            <View style={styles.avatar}><Text style={styles.avatarText}>{authorName.charAt(0).toUpperCase()}</Text></View>
            <View><Text style={styles.authorName}>{authorName}</Text><Text style={styles.authorDate}>Posted {createdDate}</Text></View>
          </View>
          {idea.resolution_notes && (
            <View style={styles.resolutionCard}>
              <Text style={styles.resolutionLabel}>Resolution Notes</Text>
              <Text style={styles.resolutionText}>{idea.resolution_notes}</Text>
              {idea.resolver && <Text style={styles.resolverText}>— {idea.resolver.display_name || idea.resolver.email}</Text>}
            </View>
          )}
        </View>

        {/* VOTE BAR */}
        <IdeaVoteBar 
          upvotes={idea.upvotes ?? 0} 
          downvotes={idea.downvotes ?? 0} 
          score={idea.score ?? 0} 
          userVote={idea.user_vote ?? null} 
          onVote={handleVote} 
          voting={voting} 
        />

        {/* TAB SELECTOR */}
        <View style={styles.tabBar}>
          <Pressable onPress={() => setActiveTab('comments')} style={[styles.tab, activeTab === 'comments' && styles.tabActive]}>
            <Ionicons name="chatbubbles-outline" size={icon.md} color={activeTab === 'comments' ? color.primary : color.textMuted} />
            <Text style={[styles.tabText, activeTab === 'comments' && styles.tabTextActive]}>Comments ({idea.comment_count ?? 0})</Text>
          </Pressable>
          <Pressable onPress={() => setActiveTab('activity')} style={[styles.tab, activeTab === 'activity' && styles.tabActive]}>
            <Ionicons name="time-outline" size={icon.md} color={activeTab === 'activity' ? color.primary : color.textMuted} />
            <Text style={[styles.tabText, activeTab === 'activity' && styles.tabTextActive]}>Activity</Text>
          </Pressable>
        </View>

        {/* TAB CONTENT */}
        <View style={styles.tabContent}>
          {activeTab === 'comments' ? (
            <IdeaComments comments={comments} loading={loadingComments} onAddComment={handleAddComment} commenting={commenting} />
          ) : (
            <IdeaActivity activities={activities} loading={loadingActivity} />
          )}
        </View>
      </ScrollView>

      {/* EDIT MODAL */}
      {idea && <EditIdeaModal visible={showEditModal} idea={idea} onClose={() => setShowEditModal(false)} onSave={handleSave} saving={updating} />}
    </ScreenContainer>
  );
}
